/**
 * Настройки листания: запись в localStorage, сортировка перехода адресом,
 * слова отбора. Часть `lib/productNav` (нарезка 14.10.26) — публичные имена те
 * же.
 *
 * Настройки живут в localStorage — тем же приёмом, что ширина панели графика
 * и свёрнутые блоки витрины: у закупщика своя привычка, и задавать её заново
 * на каждой карточке незачем.
 */

import { isValidDate, lastDays, rangeLabel } from "@/lib/dates";
import { cleanFs, fsWord } from "@/lib/firstSupply";
import {
  DIR_LABEL,
  METRIC_LABEL,
  type NavDir,
  type NavFilter,
  type NavMetric,
  type NavPlatform,
  PLATFORM_LABEL,
  type TileMetric,
  isFilter,
  isNavMetric,
  isTileMetric,
} from "@/lib/nav/labels";

/**
 * Адрес перехода «первая карточка кольца поставщика» (замечание владельца
 * 15.09.26 на «Поставщиках»: список номеров в адресе зашивал порядок, и
 * шестерёнка на такой карточке пропадала). Экран перехода берёт настройки
 * шестерёнки, спрашивает кольцо и открывает его первый товар — дальше стрелки
 * листают поставщика обычным порядком.
 *
 * `metric` переключает шестерёнку перед запросом и остаётся в ней: колонка «к
 * заказу» обязана открыть товар, которому что-то нужно, а не самый
 * заказываемый.
 *
 * ПРИЗНАК СБРОСА едет всегда (замечание владельца 23.09.26: «я кликаю по
 * поставщику с товарами, а он ставит ограничение по категории»): числа строки
 * посчитаны ПО ВСЕМУ поставщику, а сохранённый отбор шестерёнки резал кольцо по
 * категории прошлого захода — строка обещала сорок товаров, а карточка не
 * открывалась вовсе.
 */
export function startHref(supplierId: number, w: number, metric?: NavMetric): string {
  const хвост = metric ? `&${METRIC_PARAM}=${metric}` : "";
  return `/product?supplier=${supplierId}&w=${w}${хвост}&${RESET_PARAM}=1`;
}

/**
 * ПРИЗНАК СБРОСА сохранённого отбора в адресе перехода. Его несут ссылки
 * экранов, которые СЧИТАЮТ СВОЙ НАБОР САМИ, — дашборд «Фотосессия» (разметка
 * владельца 17.09.26) и строка «Поставщиков» (23.09.26): без него ячейка «48»
 * открывала «0 товаров», а поставщик с сорока карточками — «нет товаров в
 * „Платья“». Экран перехода сбрасывает отбор, ЗАПИСЫВАЕТ сброс (тулбар и
 * шестерёнка показывают правду) и убирает признак из адреса — перезагрузка и
 * «назад» второй раз не сбросят то, что человек выбрал уже на месте.
 */
export const RESET_PARAM = "reset";

/**
 * Настройки без того, что РЕЖЕТ НАБОР: категории, цвета, плана и потребности.
 * Период, показатель, площадка, порядок и плитка набор не режут и остаются —
 * это вкус человека, а не отбор. Фильтры по значению и лимит «Товаров» сбрасывает
 * разбор адреса (`readToolbar`): в этой настройке их нет.
 */
export function withoutSelection(settings: NavSettings): NavSettings {
  return { ...settings, cat: null, color: null, plan: "all", need: "all", fs: null };
}

/** «К заказу» — не воронка: окно у него — переключатель уходимости карточки,
 * площадки обе, порядок всегда «сначала больше». Окно дат, площадка и
 * направление к нему не относятся и в запрос не влияют. */
export const NEED_METRIC: NavMetric = "reorder";

/**
 * Сортировка ПЕРЕХОДА — параметр адреса карточки, а не настройка (разметка
 * владельца 17.09.26: «сортировку по умолчанию делай по количеству заказов»).
 * Клик «к заказу» на «Поставщиках» открывает кольцо в этом порядке, стрелки
 * переносят строку запроса и листают так же, а «Товары» открываются по
 * сохранённой сортировке. Спека —
 * `docs/specs/2026-09-17-products-submit-period-speed-design.md`, раздел 3.
 */
export const METRIC_PARAM = "metric";

export function metricOf(params: URLSearchParams): NavMetric | null {
  const got = params.get(METRIC_PARAM);
  return isNavMetric(got) ? got : null;
}

/** Настройка с сортировкой перехода поверх; без неё — та же запись: настройки
 *  стоят в зависимостях эффектов, и новый объект гонял бы запрос. */
export function withMetric(s: NavSettings, metric: NavMetric | null): NavSettings {
  return metric === null || metric === s.metric ? s : { ...s, metric };
}

export type NavSettings = {
  from: string;
  to: string;
  metric: NavMetric;
  platform: NavPlatform;
  dir: NavDir;
  plan: NavFilter;
  need: NavFilter;
  /** Показатели ПЛИТКИ раздела «Товары»; у стрелок карточки плиток нет, и в их
   *  запрос набор не едет (`ringParams`). Пусто — одно значение сортировки. */
  show: TileMetric[];
  /**
   * Категория каталога; `null` — все. Отбор РЕЖЕТ НАБОР, как «план» и
   * «потребность», поэтому стоит в настройке кольца: держи его только адрес
   * страницы «Товары» — и стрелки открытой карточки листали бы поставщика
   * целиком, споря с сеткой, из которой карточку открыли (16.09.26; спека —
   * `docs/specs/2026-09-16-products-category-view-select-design.md`). Применённое
   * значение страницы — в адресе (`?cat=`, `productsUrl.ts`), здесь его зеркало.
   *
   * Ноль — законное значение: это товары БЕЗ категории.
   */
  cat: number | null;
  /**
   * Основной цвет модели; `null` — все, `0` — «цвет не задан». Режет набор
   * ровно доводом категории и потому стоит здесь же, а на странице едет адресом
   * `?color=` (17.09.26; спека — `docs/specs/2026-09-17-products-select-filters-design.md`).
   */
  color: number | null;
  /**
   * Дата первой поставки на площадку, `wb:2026-10-06:2026-11-04` (граница может
   * быть пустой); `null` — без отбора (04.11.26, `lib/firstSupply.ts`). Режет
   * набор НА СЕРВЕРЕ (`fs=` у `/api/models`), поэтому стоит здесь, а не среди
   * фильтров по значению, что режет браузер. Стрелки карточки его не знают: в
   * `ringParams` он не входит, как и набор плитки.
   */
  fs: string | null;
};

/**
 * Нижняя граница даты в `windowPatch`. Нативное поле `input type="date"` при
 * наборе года по цифре отдаёт промежуточные значения вроде `"0202-07-18"` —
 * КАЛЕНДАРНО валидные (`isValidDate` их пропускает: год ≥ 100 не подпадает под
 * особый разбор `Date`), но уводящие окно почти на две тысячи лет назад до
 * того, как человек допечатает год целиком, — и ручка листания честно
 * запрашивала бы это окно.
 */
const МИНИМАЛЬНАЯ_ДАТА = "2000-01-01";

/** Правка окна из полей даты: пустая, некалендарная либо младше 2000 года дата
 * оставляет прежнюю — нативное поле отдаёт пустую строку, когда человек чистит
 * ввод, или недописанный год при наборе цифра за цифрой, а ручке листания
 * `from`/`to` обязательны. Одно правило на шестерёнку карточки и раздел
 * «Товары». */
export function windowPatch(s: NavSettings, from: string, to: string): Pick<NavSettings, "from" | "to"> {
  const валидна = (value: string) => isValidDate(value) && value >= МИНИМАЛЬНАЯ_ДАТА;
  return { from: валидна(from) ? from : s.from, to: валидна(to) ? to : s.to };
}

/** Читает ли кольцо окно уходимости карточки: показатель «к заказу» или
 * фильтр потребности — оба считают потребность по нему. */
export function readsWindow(s: NavSettings): boolean {
  return s.metric === NEED_METRIC || s.need !== "all";
}

/** Зависит ли кольцо от планов CRM: «к заказу» и оба фильтра. Такие кольца
 * забываются после записи плана на карточке. */
export function readsPlans(s: NavSettings): boolean {
  return s.metric === NEED_METRIC || s.plan !== "all" || s.need !== "all";
}

/** Включённые фильтры словами: «с планом», «без потребности». Ими подписан
 * счётчик и названа причина пустого отбора; без фильтров список пуст. */
export function filterWords(s: NavSettings): string[] {
  const words: string[] = [];
  if (s.plan !== "all") words.push(s.plan === "yes" ? "с планом" : "без плана");
  if (s.need !== "all") words.push(s.need === "yes" ? "с потребностью" : "без потребности");
  const поставка = fsWord(s.fs);
  if (поставка) words.push(поставка);
  return words;
}

export type SelectionMeta = { cat: number[] | null; color: number[] | null };
export type SelectionNames = { category: (id: number) => string; color: (id: number) => string };

/**
 * СЛОВА ВСЕГО ОТБОРА: категории, цвет и фильтры плана и потребности — одной
 * функцией на карточку, экран перехода и раздел «Товары».
 *
 * Пока категорию приклеивал у себя один «Товары», на двух экранах из трёх она
 * была невидима: карточка товара чужой категории показывала «— / 0» без единого
 * слова о причине, и владелец прочитал это как сломанный счётчик (16.09.26,
 * `/product/18820`; спека — `docs/specs/2026-09-16-nav-category-visible-design.md`).
 * Второе место, где собираются слова того же отбора, разъедется с первым на
 * следующей же настройке — потому функция и одна. Цвет называется тем же
 * правилом, что категория (17.09.26) — отбор, о котором подпись молчит,
 * неотличим от поломки.
 *
 * Категории и цвета приходят ИЗ ОТВЕТА (`meta`), а не из настроек: подпись
 * обязана называть тот отбор, по которому посчитаны видимые числа. Ответа нет
 * (запрос ещё летит либо упал) — `meta: null`, и словами остаются только
 * фильтры настроек.
 *
 * `предлог` — форма для объяснения пустоты («в «клатчи»», «цвета «синий»»); без
 * него имя стоит голым, как в подписи отбора.
 */
export function selectionWords(
  meta: SelectionMeta | null,
  s: NavSettings,
  names: SelectionNames,
  предлог = false,
): string[] {
  const категории = (meta?.cat ?? []).map((id) => (предлог ? `в «${names.category(id)}»` : names.category(id)));
  const цвета = (meta?.color ?? []).map((id) => (предлог ? `цвета «${names.color(id)}»` : names.color(id)));
  return [...категории, ...цвета, ...filterWords(s)];
}

const KEY = "supply.product-nav";

/**
 * Окно по умолчанию — 60 дней, ТО ЖЕ, на котором открывается окно продаж этой
 * же карточки (решение владельца 11.09.26: два разных дефолтных окна на одном
 * экране читались бы как одно). Период продаж стал 60 днями 15.09.26 — вместе
 * с ним и это окно; сохранённая в браузере настройка не трогается.
 *
 * Короткие окна для сортировки бесполезны: за 7 дней у 71,7 % карточек нет
 * движения вовсе (замер 02.09.26), и порядок решала бы ничья.
 */
const DEFAULT_DAYS = 60;

export function defaultSettings(): NavSettings {
  const [from, to] = lastDays(DEFAULT_DAYS);
  // Набор пуст по умолчанию: плитка показывает одно значение сортировки, как до
  // 16.09.26, — а дорогие показатели не считаются, пока их не попросили.
  return {
    from,
    to,
    metric: "orders",
    platform: "both",
    dir: "desc",
    plan: "all",
    need: "all",
    show: [],
    cat: null,
    color: null,
    fs: null,
  };
}

/** Дата в формате поля `input type="date"`, иначе браузер молча покажет пусто.
 * Проверка КАЛЕНДАРНАЯ (`isValidDate`), а не форма регуляркой: битая запись
 * хранилища вроде `"2026-13-45"` иначе ушла бы в ручку листания и держала бы
 * 422 на каждой карточке до ручной чистки localStorage. */
function дата(value: unknown, запас: string): string {
  return typeof value === "string" && isValidDate(value) ? value : запас;
}

/**
 * Версия записи настроек. До 17.09.26 экран перехода СОХРАНЯЛ «к заказу», и
 * запись без версии с этой сортировкой — почти наверняка след перехода, а не
 * выбор: она читается как «заказы» один раз. Явный выбор после этого пишется с
 * версией и запоминается как прежде.
 */
const ВЕРСИЯ = 2;

/**
 * Настройки из хранилища, ПОЛЕ ЗА ПОЛЕМ.
 *
 * Чужая запись в localStorage (старая версия, чужая вкладка, ручная правка)
 * иначе отправляла бы в ручку показатель, которого нет, и экран получал бы 422
 * на каждой карточке — навсегда, потому что чинить хранилище некому.
 */
export function loadSettings(): NavSettings {
  const запас = defaultSettings();
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return запас;
    const saved = JSON.parse(raw) as Partial<NavSettings> & { v?: unknown };
    return {
      from: дата(saved.from, запас.from),
      to: дата(saved.to, запас.to),
      metric:
        saved.v !== ВЕРСИЯ && saved.metric === NEED_METRIC
          ? запас.metric
          : saved.metric && saved.metric in METRIC_LABEL
            ? saved.metric
            : запас.metric,
      platform: saved.platform && saved.platform in PLATFORM_LABEL ? saved.platform : запас.platform,
      dir: saved.dir && saved.dir in DIR_LABEL ? saved.dir : запас.dir,
      // Запись до 15.09.26 фильтров не знает — у неё «все», прежнее поведение.
      plan: isFilter(saved.plan) ? saved.plan : запас.plan,
      need: isFilter(saved.need) ? saved.need : запас.need,
      // Чужие имена отбрасываются поштучно, а не гасят весь набор: запись из
      // старой версии вкладки иначе отправляла бы в ручку показатель, которого
      // нет, и раздел получал бы 422 навсегда — чинить хранилище некому.
      show: Array.isArray(saved.show) ? saved.show.filter(isTileMetric) : запас.show,
      // Запись до 16.09.26 категории не знает — у неё «все». Граница `>= 0`, а
      // не `> 0`: ноль значит «товары без категории», это отбор, а не пусто.
      cat:
        typeof saved.cat === "number" && Number.isInteger(saved.cat) && saved.cat >= 0
          ? saved.cat
          : запас.cat,
      // Запись до 17.09.26 цвета не знает — у неё «все». Ноль — «цвет не задан».
      color:
        typeof saved.color === "number" && Number.isInteger(saved.color) && saved.color >= 0
          ? saved.color
          : запас.color,
      // Запись до 04.11.26 отбора по первой поставке не знает; мусор — «нет».
      fs: cleanFs(saved.fs),
    };
  } catch {
    return запас;
  }
}

export function saveSettings(settings: NavSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...settings, v: ВЕРСИЯ }));
  } catch {
    // Приватное окно и запрещённое хранилище — не повод ломать листание.
  }
}

/** Подпись окна под счётчиком: «13.07 – 11.09», у чужого года — с годом. Диапазон заморожен в браузере и
 * через неделю станет старым молча — подпись единственное, что об этом скажет. */
export function windowLabel(s: NavSettings): string {
  return rangeLabel(s.from, s.to, " – ");
}
